import React from 'react';
import { TESTIMONIALS, BEHIND_THE_SCENES } from '../mock';
import { Award, Users, Sparkles } from 'lucide-react';
import ClientMarquee from '../components/ClientMarquee';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const About = () => {
  useSEO({
    title: 'About Yellow, Marketing Agency Liverpool',
    description: 'Meet Yellow. A Liverpool marketing agency dedicated to inspiring your clients and staff. Over a decade solving marketing problems for global brands and the public sector.',
    path: '/about'
  });
  return (
  <>
    <section className="relative pt-36 pb-16 lg:pt-44 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 -right-40 w-[600px] h-[600px] rounded-full bg-[#F5CD1B]/6 blur-[140px]"></div>
      </div>
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <p className="section-label mb-6">About Yellow</p>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.02] max-w-4xl">
          A team dedicated to <span className="text-[#F5CD1B]">inspiring your clients and your staff.</span>
        </h1>
        <p className="text-white/70 text-xl leading-relaxed mt-8 max-w-3xl">
          Yellow started as a media production agency and grew into a strategic partner & creative consultancy, delivering innovative strategy, video, design, digital and social strategies focused on retaining customers, not competing on price and keeping your staff excited about working for you.
        </p>
      </div>
    </section>

    <ClientMarquee />

    {/* Testimonial highlight */}
    <section className="py-24 bg-[#08080a]">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
        <p className="section-label mb-6">What clients say</p>
        <p className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.15]">
          “{TESTIMONIALS[0].quote}”
        </p>
        <p className="text-[#F5CD1B] text-sm mt-8 tracking-widest uppercase">{TESTIMONIALS[0].name}, {TESTIMONIALS[0].role}</p>
      </div>
    </section>

    {/* Story */}
    <section className="py-24">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="section-label mb-4">Our story</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-[1.05]">
            Helping you reach the <span className="text-[#F5CD1B]">goals of your brand.</span>
          </h2>
        </div>
        <div className="lg:col-span-7 text-white/75 leading-relaxed text-lg space-y-5">
          <p>Founded in Liverpool over a decade ago, Yellow was born from a simple frustration: marketing agencies charging premium prices for mediocre work (based around providing a service - not fixing your problems). We have changed our focus and goals from providing brand services to solving issues for our clients so you feel real value no matter the size of budget.</p>
          <p>Today we work with global brands like Persil, Cancer Research UK, Land Rover and the NHS, alongside ambitious SMEs and public sector organisations.</p>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-16 bg-[#08080a]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Users, title: 'Director level project management', body: 'Experienced experts will be personally working with you rather than handed to assistants.' },
            { icon: Award, title: '1,000+ projects delivered', body: 'From household name brands to Government programmes and start-ups, we’ve worked in every industry imaginable.' },
            { icon: Sparkles, title: 'Strategy & Creative', body: 'Helping you both build innovative strategies AND producing truly creative content.' }
          ].map((v, i) => (
            <div key={i} className="bg-[#111114] border border-white/10 rounded-2xl p-8 hover:border-[#F5CD1B]/30 transition-colors">
              <div className="w-12 h-12 rounded-lg bg-[#F5CD1B]/10 border border-[#F5CD1B]/25 flex items-center justify-center">
                <v.icon className="text-[#F5CD1B]" size={20} />
              </div>
              <h3 className="font-display text-2xl font-bold text-white mt-5">{v.title}</h3>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Behind the scenes */}
    <section className="py-24">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <p className="section-label mb-4">Behind the scenes</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
            Where the <span className="text-[#F5CD1B]">work happens.</span>
          </h2>
        </div>
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4">
          {BEHIND_THE_SCENES.map((item) => (
            <div key={item.id} className="group relative block w-full overflow-hidden rounded-xl bg-[#111114] border border-white/5 break-inside-avoid hover:border-[#F5CD1B]/30 transition-colors">
              <img src={item.src} alt={item.alt} className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>

    <CTASection />
  </>
  );
};

export default About;
